/**
 * @fileoverview React context that handles frontend configuration loaded from config files.
 *
 * This provides frontend config (APP, JOBS, RESUME, etc.) via React Context,
 * replacing the removed Next.js runtime config options.
 *
 * @format
 */

import { createContext, useContext, useMemo } from 'react';
import {
  mergeFrontendConfig,
  parseFrontendOverridesFromDocumentCookie,
} from '../lib/frontendConfigOverrides';

/**
 * Frontend configuration exposed to React components.
 */
export interface FrontendConfigContextType {
  /**
   * App configuration.
   */
  APP: any;
  /**
   * Jobs configuration.
   */
  JOBS: any;
  /**
   * Resume configuration.
   */
  RESUME: any;
  /**
   * Organizations configuration.
   */
  ORGANIZATIONS: any;
  /**
   * User configuration.
   */
  USER: any;
  /**
   * Wizard configuration.
   */
  WIZARD: any;
  /**
   * Public API keys.
   */
  API_KEYS: any;
  /**
   * Current frontend key.
   */
  frontend: string;
}

export const DEFAULT_FRONTEND_CONFIG: FrontendConfigContextType = {
  APP: {},
  JOBS: {},
  RESUME: {},
  ORGANIZATIONS: {},
  USER: {},
  WIZARD: {},
  API_KEYS: {},
  frontend: 'default',
};

let currentFrontendConfig: FrontendConfigContextType = DEFAULT_FRONTEND_CONFIG;

/**
 * Frontend config context.
 */
export const FrontendConfigContext = createContext<FrontendConfigContextType>(
  DEFAULT_FRONTEND_CONFIG,
);

interface FrontendConfigProviderProps {
  /**
   * The children elements.
   */
  children: React.ReactNode;
  /**
   * Frontend configuration object.
   */
  config: FrontendConfigContextType;
}

/**
 * Provider component for frontend configuration.
 */
export const FrontendConfigProvider = ({
  children,
  config,
}: FrontendConfigProviderProps) => {
  const effectiveConfig = useMemo(() => {
    const browserOverrides = parseFrontendOverridesFromDocumentCookie();
    return mergeFrontendConfig(config, browserOverrides);
  }, [config]);

  currentFrontendConfig = effectiveConfig;

  return (
    <FrontendConfigContext.Provider value={effectiveConfig}>
      {children}
    </FrontendConfigContext.Provider>
  );
};

/**
 * Hook to use frontend configuration context.
 */
export const useFrontendConfig = (): FrontendConfigContextType => {
  return useContext(FrontendConfigContext);
};

/**
 * Synchronous accessor for runtime frontend config in non-React utilities.
 */
export const getCurrentFrontendConfig = (): FrontendConfigContextType => {
  return currentFrontendConfig;
};
